import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import WorkplacesService from '../../../../../../services/WorkplacesService';
import floatToCurrency from '../../../../../../utils/floatToCurrency';
import { TransfersBehaviorType } from '../../../../../Company/Settings/types';
import { WorkplaceFromApiObject } from '../../../../types';

interface UseLoadFormI {
  setName: Dispatch<SetStateAction<string>>;
  setWorkplaceCode: Dispatch<SetStateAction<string>>;
  setCep: Dispatch<SetStateAction<string>>;
  setStreetName: Dispatch<SetStateAction<string>>;
  setNumber: Dispatch<SetStateAction<number | string>>;
  setComplement: Dispatch<SetStateAction<string>>;
  setDistrict: Dispatch<SetStateAction<string>>;
  setCity: Dispatch<SetStateAction<string>>;
  setUf: Dispatch<SetStateAction<string>>;
  setIsGettingCepInfo: Dispatch<SetStateAction<boolean>>;
  setLat: Dispatch<SetStateAction<string | number>>;
  setLng: Dispatch<SetStateAction<string | number>>;
  setHasAffreightment: Dispatch<SetStateAction<boolean>>;
  setOperator: Dispatch<SetStateAction<{ value: string, label: string }>>;
  setUsesVtAndAffreightment: Dispatch<SetStateAction<boolean>>;
  setWalkDistance: Dispatch<SetStateAction<number[]>>;
  setSelectedWalkDistance: Dispatch<SetStateAction<number[]>>;
  setCompareWithDesiredVtValue: Dispatch<SetStateAction<boolean>>;
  setDesiredVtValue: Dispatch<SetStateAction<string>>;
  setSameWalkDistanceFromCompany: Dispatch<SetStateAction<boolean>>;
  setSameDesiredVtValueFromCompany: Dispatch<SetStateAction<boolean>>;
  setHasKmLimit: Dispatch<SetStateAction<boolean>>;
  setKmLimit: Dispatch<SetStateAction<number[]>>;
  setSelectedKmLimit: Dispatch<SetStateAction<number[]>>;
  setKindOfVtSpTrans: Dispatch<SetStateAction<'common' | 'vt'>>;
  setIntegratesWithRiocard: Dispatch<SetStateAction<boolean>>;
  setIsActive: Dispatch<SetStateAction<boolean>>;
  setTransfersBehavior: Dispatch<SetStateAction<TransfersBehaviorType>>;
  setCompanyTransfersBehavior: Dispatch<SetStateAction<TransfersBehaviorType>>;
  setSameTransfersBehaviorFromCompany: Dispatch<SetStateAction<boolean>>;
}

type OperatorFromApiObject = {
  codOp: number;
  opFretado: string;
}

export default function useLoadForm({
  setName,
  setWorkplaceCode,
  setCep,
  setStreetName,
  setNumber,
  setComplement,
  setDistrict,
  setCity,
  setUf,
  setLat,
  setLng,
  setHasAffreightment,
  setOperator,
  setUsesVtAndAffreightment,
  setWalkDistance,
  setSelectedWalkDistance,
  setCompareWithDesiredVtValue,
  setDesiredVtValue,
  setSameWalkDistanceFromCompany,
  setSameDesiredVtValueFromCompany,
  setHasKmLimit,
  setKmLimit,
  setSelectedKmLimit,
  setKindOfVtSpTrans,
  setIntegratesWithRiocard,
  setIsActive,
  setTransfersBehavior,
  setCompanyTransfersBehavior,
  setSameTransfersBehaviorFromCompany,
}: UseLoadFormI) {
  const [isLoading, setIsLoading] = useState(false);
  const [operatorsOptions, setOperatorsOptions] = useState([{ value: '', label: '' }]);

  const { apiCall } = useApiCall();
  const { companyCode, userCode, userProfile } = useGetGeneralAppData();
  const { workplaceId } = useParams();
  const navigate = useNavigate();

  const getOperatorsOptions = useCallback(async () => {
    await apiCall({
      apiToCall: WorkplacesService.getAffreightmentOperators,
      queryParams: {
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
      },
      catchMessage: 'Não foi possível abrir o formulário. Por favor, tente novamente',
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.codigo && !(apiResponse.msg === 'Não existem Operadoras de Fretados cadastradas na empresa.')) {
          toast.error('Não foi possível abrir o formulário. Por favor, tente novamente');
          navigate('/workplaces');
          return;
        }
        const operatorsListFromApi: OperatorFromApiObject[] = apiResponse.Opfretado;

        if (!operatorsListFromApi || operatorsListFromApi?.length === 0) {
          setOperatorsOptions([{ value: '', label: ''}]);
        }
        const operatorsMapped = operatorsListFromApi?.map((op) => (
          { value: String(op.codOp), label: `${op.codOp} - ${op.opFretado}` }
        ));

        setOperatorsOptions(operatorsMapped);
      },
      catchAction: () => {
        navigate('/workplaces');
      }
    });
  }, [apiCall, companyCode, navigate, userCode, userProfile]);

  const getWorkplaceDetails = useCallback(async () => {
    await apiCall({
      apiToCall: WorkplacesService.getWorkplace,
      queryParams: {
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        workplaceId,
      },
      catchMessage: 'Não foi possível abrir o formulário. Por favor, tente novamente',
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.codigo) {
          toast.error(`Não foi possível abrir o formulário. Por favor, tente novamente (${apiResponse.msg})`);
          navigate('/workplaces');
          return;
        }

        if (!apiResponse.codRetorno) {
          toast.warn(`Não foi possível abrir o formulário. Por favor, tente novamente (${apiResponse.msg})`);
          navigate('/workplaces');
          return;
        }

        const workplaceDetails: WorkplaceFromApiObject = apiResponse.dadosEndereco[0];

        setName(workplaceDetails.denominacaoLT);
        setWorkplaceCode(workplaceDetails.codEndNoclienteAlfaN);
        setCep(workplaceDetails.cep);
        setStreetName(workplaceDetails.logradouro);
        setNumber(workplaceDetails.numero);
        setComplement(workplaceDetails.complemento);
        setDistrict(workplaceDetails.bairro);
        setCity(workplaceDetails.cidade);
        setUf(workplaceDetails.UF);
        setLat(workplaceDetails.lat);
        setLng(workplaceDetails.lng);
        setHasAffreightment(workplaceDetails.temFretamento);
        setOperator({ value: String(workplaceDetails.codFreta), label: String(workplaceDetails.codFreta) });
        setUsesVtAndAffreightment(!(workplaceDetails.opcaoVTFreta));
        setWalkDistance([500, workplaceDetails.distancia < 500 ? 500 : workplaceDetails.distancia]);
        setSelectedWalkDistance([500, workplaceDetails.distancia < 500 ? 500 : workplaceDetails.distancia]);
        setCompareWithDesiredVtValue(!!workplaceDetails.valorTeto);
        setDesiredVtValue(floatToCurrency(workplaceDetails.valorTeto));
        setSameWalkDistanceFromCompany(!workplaceDetails.distancia);
        setSameDesiredVtValueFromCompany(!workplaceDetails.valorTeto);

        setHasKmLimit(workplaceDetails.temLimitadorKM);
        setKmLimit([5, workplaceDetails.KM < 5 ? 5 : (workplaceDetails.KM > 1000 ? workplaceDetails.KM / 1000 : workplaceDetails.KM)]);
        setSelectedKmLimit([5, workplaceDetails.KM < 5 ? 5 : (workplaceDetails.KM > 1000 ? workplaceDetails.KM / 1000 : workplaceDetails.KM)]);
        setKindOfVtSpTrans(workplaceDetails.semTaxaSPtrans ? 'common' : 'vt');
        setIntegratesWithRiocard(!workplaceDetails.semIntegrarRJ);
        setIsActive(workplaceDetails.endAtivo);

        const transfersBehaviorMap: { [key: number]: TransfersBehaviorType } = {
          0: 'unlimited',
          1: 'minimun',
          2: 'default',
        };
        const companyBehavior = transfersBehaviorMap[workplaceDetails.tipoBaldeacaoEmp] ?? 'default';
        setCompanyTransfersBehavior(companyBehavior);
        const isSameAsCompany = workplaceDetails.tipoBaldeacao === workplaceDetails.tipoBaldeacaoEmp;
        setSameTransfersBehaviorFromCompany(isSameAsCompany);
        setTransfersBehavior(isSameAsCompany ? companyBehavior : (transfersBehaviorMap[workplaceDetails.tipoBaldeacao] ?? 'default'));
      },
      catchAction: () => {
        navigate('/workplaces');
      }
    });
  }, [apiCall, companyCode, navigate, setCep, setCity, setCompanyTransfersBehavior, setCompareWithDesiredVtValue, setComplement, setDesiredVtValue, setDistrict, setHasAffreightment, setHasKmLimit, setIntegratesWithRiocard, setIsActive, setKindOfVtSpTrans, setKmLimit, setLat, setLng, setName, setNumber, setOperator, setSameDesiredVtValueFromCompany, setSameTransfersBehaviorFromCompany, setSameWalkDistanceFromCompany, setSelectedKmLimit, setSelectedWalkDistance, setStreetName, setTransfersBehavior, setUf, setUsesVtAndAffreightment, setWalkDistance, setWorkplaceCode, userCode, userProfile, workplaceId]);

  const loadForm = useCallback(async () => {
    try {
      setIsLoading(true);
      await getOperatorsOptions();
      if (workplaceId) {
        await getWorkplaceDetails();
      }
    } catch (error) {
      toast.error(`Não foi possível carregar o formulário. Por favor, tente novamente (${error})`);
      navigate('/workplaces');
    } finally {
      setIsLoading(false);
    }
  }, [getOperatorsOptions, getWorkplaceDetails, navigate, workplaceId]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  return {
    isLoading,
    setIsLoading,
    operatorsOptions,
  };
}
